import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { EMPTY_PERFORMANCE, toNum } from "./performance.server";

export interface PerformanceStats {
  total: number;
  resolved: number;
  wins: number;
  losses: number;
  expired: number;
  tpHits: number;
  slHits: number;
  winRatePct: number;
  avgRR: number;
  avgConviction: number;
  avgDrawdownPct: number | null;
  bestPair: string | null;
  worstPair: string | null;
  updatedAt: string | null;
  /** "verified" once >=30 resolved market views, otherwise "insufficient_data". */
  status: "verified" | "insufficient_data";
}

/** Minimum number of resolved market views before any performance number is shown. */
export const MIN_RESOLVED_FOR_STATS = 30;

export const getPerformanceStats = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  // Prefer the immutable snapshot ledger; fall back to legacy site_settings if absent.
  const { data: snap } = await supabaseAdmin
    .from("performance_snapshots")
    .select("*")
    .order("snapshot_date", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (snap) {
    const resolved = Number(snap.resolved_views ?? 0);
    if (resolved < MIN_RESOLVED_FOR_STATS) {
      return {
        ...EMPTY_PERFORMANCE,
        resolved,
        total: Number(snap.total_views ?? 0),
        updatedAt: snap.computed_at ?? null,
        status: "insufficient_data" as const,
      } satisfies PerformanceStats;
    }
    const wins = Number(snap.win_count ?? 0);
    const losses = Number(snap.loss_count ?? 0);
    return {
      total: Number(snap.total_views ?? 0),
      resolved,
      wins,
      losses,
      expired: Number(snap.expired_count ?? 0),
      tpHits: wins,
      slHits: losses,
      winRatePct: Number(snap.win_rate ?? 0),
      avgRR: Number(snap.avg_rr ?? 0),
      avgConviction: 0,
      avgDrawdownPct: null,
      bestPair: (snap.best_pair as string | null) ?? null,
      worstPair: (snap.worst_pair as string | null) ?? null,
      updatedAt: snap.computed_at ?? null,
      status: "verified" as const,
    } satisfies PerformanceStats;
  }

  // Legacy fallback — old site_settings store. Always gated on resolved count.
  const { data } = await supabaseAdmin
    .from("site_settings")
    .select("value, updated_at")
    .eq("key", "performance_stats")
    .maybeSingle();
  if (!data?.value) {
    return { ...EMPTY_PERFORMANCE, status: "insufficient_data" as const } satisfies PerformanceStats;
  }
  const v = data.value as Record<string, unknown>;
  const resolved = toNum(v.resolved) || 0;
  const legacy: PerformanceStats = {
    total: toNum(v.total) || 0,
    resolved,
    wins: toNum(v.wins) || 0,
    losses: toNum(v.losses) || 0,
    expired: toNum(v.expired) || 0,
    tpHits: toNum(v.tpHits) || 0,
    slHits: toNum(v.slHits) || 0,
    winRatePct: toNum(v.winRatePct) || 0,
    avgRR: toNum(v.avgRR) || 0,
    avgConviction: toNum(v.avgConviction) || 0,
    avgDrawdownPct: v.avgDrawdownPct == null ? null : toNum(v.avgDrawdownPct),
    bestPair: (v.bestPair as string | null) ?? null,
    worstPair: (v.worstPair as string | null) ?? null,
    updatedAt: data.updated_at ?? null,
    status: resolved >= MIN_RESOLVED_FOR_STATS ? "verified" : "insufficient_data",
  };
  if (legacy.status === "insufficient_data") {
    return {
      ...EMPTY_PERFORMANCE,
      resolved,
      total: legacy.total,
      updatedAt: legacy.updatedAt,
      status: "insufficient_data" as const,
    } satisfies PerformanceStats;
  }
  return legacy;
});

/**
 * Recompute aggregates from the market_view_ledger and persist a daily
 * snapshot. WAIT/cancelled/degraded views are excluded from accuracy.
 * Safe to call from cron with the service role; admin-gated when invoked
 * via the authenticated server-fn path.
 */
export async function recomputePerformanceSnapshot(): Promise<PerformanceStats> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  const { data: ledger, error } = await supabaseAdmin
    .from("market_view_ledger")
    .select("pair, final_view, outcome, pips_result, degraded_flags, risk_zone_target, invalidation, reference_zone")
    .limit(50000);
  if (error) throw new Error(error.message);

  const rows = ledger ?? [];
  // Honest accuracy basis: directional, resolved as win/loss, AND not flagged degraded.
  const directional = rows.filter((r) => r.final_view === "BUY" || r.final_view === "SELL");
  const isDegraded = (r: { degraded_flags?: unknown }) => {
    const f = (r.degraded_flags ?? {}) as Record<string, unknown>;
    return Boolean(f.sentiment_degraded || f.council_degraded || f.any_degraded);
  };
  const clean = directional.filter((r) => !isDegraded(r));
  const wins = clean.filter((r) => r.outcome === "win").length;
  const losses = clean.filter((r) => r.outcome === "loss").length;
  const expired = clean.filter((r) => r.outcome === "expired").length;
  const degraded = directional.filter(isDegraded).length;
  const resolved = wins + losses;
  const winRate = resolved > 0 ? Math.round((wins / resolved) * 1000) / 10 : 0;

  // Reward:risk = |target - entry| / |entry - invalidation|, averaged over directional
  // rows where both distances are positive.
  const rrs = directional
    .map((r) => {
      const e = Number(r.reference_zone);
      const t = Number(r.risk_zone_target);
      const sl = Number(r.invalidation);
      if (!Number.isFinite(e) || !Number.isFinite(t) || !Number.isFinite(sl)) return null;
      const risk = Math.abs(e - sl);
      const reward = Math.abs(t - e);
      if (risk <= 0 || reward <= 0) return null;
      return reward / risk;
    })
    .filter((v): v is number => v != null);
  const avgRR = rrs.length > 0 ? Math.round((rrs.reduce((a, b) => a + b, 0) / rrs.length) * 100) / 100 : 0;
  const totalPips = clean.reduce((acc, r) => acc + (Number(r.pips_result) || 0), 0);

  // Best / worst pair by win rate (min 5 resolved on that pair).
  const byPair = new Map<string, { wins: number; resolved: number }>();
  for (const r of clean) {
    if (r.outcome !== "win" && r.outcome !== "loss") continue;
    const cur = byPair.get(r.pair) ?? { wins: 0, resolved: 0 };
    cur.resolved += 1;
    if (r.outcome === "win") cur.wins += 1;
    byPair.set(r.pair, cur);
  }
  let bestPair: string | null = null;
  let bestPct = -1;
  let worstPair: string | null = null;
  let worstPct = 101;
  for (const [pair, v] of byPair) {
    if (v.resolved < 5) continue;
    const pct = (v.wins / v.resolved) * 100;
    if (pct > bestPct) { bestPct = pct; bestPair = pair; }
    if (pct < worstPct) { worstPct = pct; worstPair = pair; }
  }

  const today = new Date().toISOString().slice(0, 10);
  const computedAt = new Date().toISOString();
  const { error: upErr } = await supabaseAdmin
    .from("performance_snapshots")
    .upsert(
      {
        snapshot_date: today,
        total_views: rows.length,
        resolved_views: resolved,
        win_count: wins,
        loss_count: losses,
        expired_count: expired,
        degraded_count: degraded,
        win_rate: winRate,
        avg_rr: avgRR,
        total_pips: totalPips,
        best_pair: bestPair,
        worst_pair: worstPair,
        computed_at: computedAt,
      } as never,
      { onConflict: "snapshot_date" },
    );
  if (upErr) throw new Error(upErr.message);

  return {
    total: rows.length,
    resolved,
    wins,
    losses,
    expired,
    tpHits: wins,
    slHits: losses,
    winRatePct: winRate,
    avgRR,
    avgConviction: 0,
    avgDrawdownPct: null,
    bestPair,
    worstPair,
    updatedAt: computedAt,
    status: resolved >= MIN_RESOLVED_FOR_STATS ? "verified" : "insufficient_data",
  };
}

/** Admin-only entrypoint for the recompute job. */
export const refreshPerformanceStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: roles } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    const isAdmin = (roles ?? []).some((r) => r.role === "admin");
    if (!isAdmin) throw new Error("Forbidden");
    return recomputePerformanceSnapshot();
  });
